import { createSelector } from 'reselect';
import {
  ALL_EVENT_NAMES_WITH_LOCAL_CURRENCY,
  DATASOURCES,
  STANDARD_EVENT_NAMES_WITH_LOCAL_CURRENCY,
} from '../../../common/support/eo_mapping';
import { ANALYTICS_CONTEXT, ALL_GRANULARITY, DAY_GRANULARITY } from '../../../../../shared/constants/analytics';
import { ADS_ANALYTICS_NAMESPACE } from '../../../../../shared/constants';
import { handleGetDateRangeInfo } from '../../support/dateUtils';
import { regenerateSelectedColumns } from '../../../common/support/utils';
import type { AdsShareableData, ShareableData } from '../../../common/types/shareableTypes';
import { getCurrentAppLocalCurrency } from '../AppSelectors';
import {
  createCalculationColumnsSelector,
  createCustomEventColumnsSelector,
  createDatasourceColumnsSelector,
  createDimensionColumnsSelector,
  createStandardEventColumnsSelector,
} from '../Analytics';
import { TOTAL_COUNT_METRIC, UNIQUE_COUNT_METRIC } from '../../constants';
import { ADS_CALCULATIONS, ADS_DATASOURCES } from './constants';
import { getCustomCalculationColumns } from '../CustomCalculationSelectors';
import type { NewDateRangeInfo } from '../../types/dateRangeTypes';
import { handleGetNewDateRangeInfo } from '../../support/Dates/dateUtils';
import { CALCULATED_EVENT_NAMES_WITH_LOCAL_CURRENCY } from '../../../common/support/eo_mapping/event_names';
import type { RootState } from 'client/js/redux';

const EMPTY_SHAREABLE = {} as ShareableData;
const EMPTY_NAMESPACE = {} as AdsShareableData;
const EMPTY_TABLE_STATE = {} as AdsShareableData[typeof ANALYTICS_CONTEXT.ADS_TABLE];
const EMPTY_TRENDS_STATE = {} as AdsShareableData[typeof ANALYTICS_CONTEXT.ADS_TRENDS];
const EMPTY_ARRAY = [];

const getShareableStateData = (state: RootState): ShareableData => state.shareable.data || EMPTY_SHAREABLE;

export const selectAdsShareableState: (state: RootState) => AdsShareableData = createSelector(
  getShareableStateData,
  (shareableStateData) => shareableStateData[ADS_ANALYTICS_NAMESPACE] || EMPTY_NAMESPACE
);

const selectReportTableState = createSelector(
  selectAdsShareableState,
  (adsShareableState) => adsShareableState[ANALYTICS_CONTEXT.ADS_TABLE] || EMPTY_TABLE_STATE
);

const selectTrendsState = createSelector(
  selectAdsShareableState,
  (adsShareableState) => adsShareableState[ANALYTICS_CONTEXT.ADS_TRENDS] || EMPTY_TRENDS_STATE
);

export const selectReportGranularity = createSelector(selectReportTableState, (reportTableState) => {
  return reportTableState?.granularity || ALL_GRANULARITY;
});

export const selectTrendsGranularity = createSelector(
  selectTrendsState,
  (trendsState) => trendsState?.granularity || DAY_GRANULARITY
);

export const selectReportMetric = createSelector(selectReportTableState, (reportTableState) => {
  return reportTableState?.unique ? UNIQUE_COUNT_METRIC : TOTAL_COUNT_METRIC;
});

const getReportQuery = createSelector(selectReportTableState, (reportTableState) => {
  return reportTableState?.query;
});

const getReportDimensions = createSelector(getReportQuery, (reportQuery) => {
  return reportQuery?.dimensions || EMPTY_ARRAY;
});

const getDatasources = () => ADS_DATASOURCES;

const getCalculations = () => ADS_CALCULATIONS;

const getEvents = () => Object.keys(STANDARD_EVENT_NAMES_WITH_LOCAL_CURRENCY);

const getColumnOptions = createSelector(getCurrentAppLocalCurrency, (localCurrency?: string) => {
  return { localCurrency };
});

export const selectDateRangeInfo = createSelector(selectAdsShareableState, (adsShareableState) => {
  const { dateRange, defaultRange } = adsShareableState;
  const dateRangeInfo = {
    dateRange,
    defaultRange,
  };

  return handleGetDateRangeInfo(dateRangeInfo);
});

export const selectNewDateRangeInfo = createSelector(selectAdsShareableState, (adsShareableState) => {
  const { dateRange, defaultRange } = adsShareableState as NewDateRangeInfo;
  return handleGetNewDateRangeInfo({
    dateRange,
    defaultRange,
  });
});

export const selectDimensionColumns = createDimensionColumnsSelector(getReportDimensions);
export const selectDatasourceColumns = createDatasourceColumnsSelector(getDatasources, getColumnOptions);
export const selectCalculationColumns = createCalculationColumnsSelector(getCalculations, getColumnOptions);
export const selectStandardEventColumns = createStandardEventColumnsSelector(getEvents, getColumnOptions);
export const selectCustomEventColumns = createCustomEventColumnsSelector();

export const selectAllColumns = createSelector(
  [
    selectDimensionColumns,
    selectDatasourceColumns,
    selectCalculationColumns,
    selectStandardEventColumns,
    selectCustomEventColumns,
    getCustomCalculationColumns,
  ],
  (dimensionColumns, datasourceColumns, calculationColumns, eventColumns, customEventColumns, customCalculationColumns) => [
    ...dimensionColumns,
    ...datasourceColumns,
    ...calculationColumns,
    ...eventColumns,
    ...customEventColumns,
    ...customCalculationColumns,
  ]
);

export const selectSelectedColumns = createSelector(
  selectAdsShareableState,
  selectAllColumns,
  (adsShareableState, allColumns) => {
    return regenerateSelectedColumns(adsShareableState.selectedColumns, allColumns);
  }
);

export const selectDatasourcesForFilter = createSelector(getShareableStateData, () => Object.keys(DATASOURCES));

const selectDatasourceTopics = () => Object.keys(ALL_EVENT_NAMES_WITH_LOCAL_CURRENCY);

const selectCalculationTopics = () => Object.keys(CALCULATED_EVENT_NAMES_WITH_LOCAL_CURRENCY);

export const selectTopicsList = createSelector(
  selectDatasourceTopics,
  selectCalculationTopics,
  (datasourceTopics, calculationTopics) => {
    return [...datasourceTopics, ...calculationTopics].filter(
      (topic) => !['PAGEVIEW', 'BRANCH_CTA_VIEW', 'JOURNEYS_DISMISSAL'].includes(topic)
    );
  }
);
